import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Chip, StatusChip } from "@/components/StatusChip";
import { ChannelBadge, channelMeta, CHANNEL_META } from "@/components/ChannelBadge";
import {
  ArrowLeft, Send, Loader2, Paperclip, TrendingUp, ShieldAlert,
  ReceiptText, CircleCheck, ArrowUpRight, GitBranch, ChevronDown, Pencil, Check,
} from "lucide-react";
import { api, fmtUSD, fmtTime } from "@/lib/api";
import { PersonAvatar } from "@/components/PersonAvatar";
import { AriaMark } from "@/components/AriaMark";
import { toast } from "sonner";

const RATING_TONE = { Excellent: "green", Healthy: "green", Watch: "amber", "At risk": "red" };
const SCORE_COLOR = { Excellent: "#22C55E", Healthy: "#4ADE80", Watch: "#F59E0B", "At risk": "#EF4444" };

const ChannelChoice = ({ value }) => {
  const m = channelMeta(value);
  const Icon = m.icon;
  return (
    <span className="flex items-center gap-1.5" style={{ color: m.color }}>
      <Icon size={13} /> <span className="text-[12px] text-white">{m.label}</span>
    </span>
  );
};

const MessageBubble = ({ msg, employee }) => {
  const isEmployee = msg.role === "employee";
  const isController = msg.role === "controller";
  const align = isEmployee ? "flex-row-reverse" : "";

  const avatar = isEmployee ? (
    <PersonAvatar name={employee.name} size={28} color={employee.avatar_color} className="mt-1" />
  ) : isController ? (
    <PersonAvatar name="Ava Thompson" size={28} className="mt-1" />
  ) : (
    <AriaMark size={28} className="mt-1" />
  );

  const bubbleClass = isEmployee
    ? "bg-[#1f1f1f] text-[#e5e5e5] border border-[#2a2a2a]"
    : isController
    ? "bg-[rgba(245,158,11,0.07)] text-[#e5e5e5] border border-[rgba(245,158,11,0.28)]"
    : "bg-[#141414] text-[#e5e5e5] border border-[#222]";

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
      className={`flex gap-2.5 ${align}`} data-testid={`thread-msg-${msg.role}`}>
      {avatar}
      <div className={`max-w-[74%] ${isEmployee ? "items-end text-right" : ""} flex flex-col`}>
        <div className={`mb-1 flex items-center gap-1.5 ${isEmployee ? "flex-row-reverse" : ""}`}>
          <span className="text-[10.5px] font-medium text-[#c9c9c9]">
            {isEmployee ? employee.name.split(" ")[0] : isController ? (msg.author || "Ava Thompson") : "Aria"}
          </span>
          <ChannelBadge channel={msg.channel} />
          {isController && <span className="text-[9.5px] text-[#FBBF24]">took over</span>}
        </div>
        <div className={`inline-block rounded-2xl px-3.5 py-2.5 text-left text-[13px] leading-relaxed ${bubbleClass}`}>
          {msg.attachment && (
            <div className="mb-1.5 flex items-center gap-1.5 rounded-lg border border-[#2a2a2a] bg-[#0d0d0d] px-2 py-1 text-[11px] text-[#A3A3A3]">
              <Paperclip size={11} /> {msg.attachment.name}
            </div>
          )}
          {msg.text}
        </div>
        <div className={`mt-1 text-[9.5px] text-[#555] ${isEmployee ? "pr-1" : "pl-1"}`}>{msg.ts ? fmtTime(msg.ts) : ""}</div>
      </div>
    </motion.div>
  );
};

const Metric = ({ label, value, tone = "neutral", testId }) => {
  const color = { green: "#4ADE80", amber: "#FBBF24", red: "#F87171", blue: "#7FAEF9", neutral: "#ffffff" }[tone];
  return (
    <div className="rounded-xl border border-[#1f1f1f] bg-[#0d0d0d] px-3 py-2.5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#6b6b6b]">{label}</div>
      <div className="mt-1 text-[16px] font-semibold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
};

export const EmployeeCommand = ({ id }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [thread, setThread] = useState(null);
  const [channel, setChannel] = useState("slack");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const bottomRef = useRef(null);

  const scrollDown = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);

  useEffect(() => {
    setData(null);
    api.get(`/employees/${id}/detail`).then((r) => {
      setData(r.data);
      setThread(r.data.thread);
      setStrategy(r.data.strategy);
      setChannel(r.data.thread?.primary_channel || "slack");
      scrollDown();
    }).catch(() => toast.error("Could not load this employee"));
    api.get("/strategies").then((r) => setStrategies(r.data.strategies)).catch(() => {});
  }, [id]);

  const switchStrategy = async (s) => {
    if (strategy && s.id === strategy.id && !strategy.inherited_default) return;
    try {
      const r = await api.post(`/employees/${id}/strategy`, { strategy_id: s.id });
      setStrategy(r.data.strategy);
      setThread((t) => ({ ...t, messages: [...t.messages, r.data.message] }));
      toast.success(`Strategy switched to "${s.name}"`);
      scrollDown();
    } catch {
      toast.error("Could not switch strategy");
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/employees/${id}/message`, { channel, text });
      setThread((t) => ({ ...t, messages: [...t.messages, r.data.message] }));
      setInput("");
      toast.success(`Sent to ${data.employee.name.split(" ")[0]} via ${channelMeta(channel).label}`);
      scrollDown();
    } catch {
      toast.error("Message could not be sent");
    } finally {
      setSending(false);
    }
  };

  if (!data || !thread) {
    return (
      <div className="space-y-4" data-testid="employee-command-loading">
        <Skeleton className="h-9 w-48 bg-[#141414]" />
        <div className="grid gap-4 xl:grid-cols-[1fr_340px]">
          <Skeleton className="h-[70vh] rounded-[14px] bg-[#141414]" />
          <Skeleton className="h-[70vh] rounded-[14px] bg-[#141414]" />
        </div>
      </div>
    );
  }

  const { employee, performance } = data;
  const first = employee.name.split(" ")[0];
  const scoreColor = SCORE_COLOR[performance.rating] || "#4F8EF7";
  const maxCat = Math.max(1, ...performance.top_categories.map((c) => c.amount));
  const composerPlaceholder = channel === "call"
    ? `Log what you discussed with ${first} on the call…`
    : `Message ${first} via ${channelMeta(channel).label}…`;

  return (
    <div className="space-y-4" data-testid="employee-command-page">
      {/* header */}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => navigate("/communications")} data-testid="employee-back-button"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#2a2a2a] text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white">
          <ArrowLeft size={15} />
        </button>
        <PersonAvatar name={employee.name} size={40} color={employee.avatar_color} />
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold tracking-tight">{employee.name}</h2>
            <Chip tone={RATING_TONE[performance.rating] || "neutral"} testId="employee-rating-chip">{performance.rating}</Chip>
          </div>
          <p className="text-[11.5px] text-[#A3A3A3]">{employee.role} · {employee.department} · reports to {employee.manager}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="employee-strategy-chip"
                className="flex items-center gap-1.5 rounded-lg border border-[rgba(79,142,247,0.28)] bg-[rgba(79,142,247,0.08)] px-2.5 py-1.5 text-[11.5px] text-[#cfe0fb] transition-colors hover:bg-[rgba(79,142,247,0.16)]">
                <GitBranch size={12} className="text-[#7FAEF9]" />
                <span className="hidden sm:inline text-[#7FAEF9]">Strategy:</span>
                <span className="max-w-[140px] truncate font-medium">{strategy?.name || "Default"}</span>
                <ChevronDown size={11} className="text-[#6b6b6b]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 border-[#222] bg-[#161616] text-white" data-testid="employee-strategy-menu">
              <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-[#6b6b6b]">
                How Aria chases {first}
              </DropdownMenuLabel>
              {strategies.map((s) => (
                <DropdownMenuItem key={s.id} onClick={() => switchStrategy(s)} data-testid={`employee-strategy-option-${s.id}`}
                  className="flex items-start gap-2 text-[12px] focus:bg-[#1f1f1f] focus:text-white">
                  <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    {strategy?.id === s.id && !strategy?.inherited_default && <Check size={12} className="text-[#4ADE80]" />}
                    {strategy?.id === s.id && strategy?.inherited_default && <span className="h-1.5 w-1.5 rounded-full bg-[#6b6b6b]" title="Inherited default" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{s.name}{s.is_default ? " · default" : ""}</span>
                    <span className="block text-[10px] text-[#6b6b6b]">{s.steps.map((st) => st.channel).join(" → ")}</span>
                  </span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-[#222]" />
              <DropdownMenuItem onClick={() => navigate(`/communications?tab=strategies${strategy ? `&edit=${strategy.id}` : ""}`)}
                data-testid="employee-strategy-edit-link"
                className="gap-2 text-[12px] text-[#7FAEF9] focus:bg-[#1f1f1f] focus:text-[#7FAEF9]">
                <Pencil size={11} /> Adjust steps in the builder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={() => navigate(`/expenses?q=${encodeURIComponent(employee.name)}`)}
            data-testid="employee-view-expenses"
            className="flex items-center gap-1 rounded-lg border border-[#2a2a2a] px-2.5 py-1.5 text-[11.5px] text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white">
            View expenses <ArrowUpRight size={12} />
          </button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_340px]">
        {/* multimodal thread */}
        <div className="flex h-[calc(100vh-300px)] min-h-[420px] flex-col rounded-[14px] border border-[#222] bg-[#111111] card-shadow xl:h-[calc(100vh-190px)]" data-testid="employee-thread">
          <div className="flex items-center justify-between border-b border-[#1e1e1e] px-4 py-3">
            <div className="flex items-center gap-2">
              <AriaMark size={16} />
              <span className="text-[13px] font-medium">Aria ↔ {first}</span>
              <span className="hidden text-[10.5px] text-[#6b6b6b] sm:inline">· the thread {first} sees</span>
            </div>
            <div className="hidden items-center gap-1 sm:flex">
              {Object.keys(CHANNEL_META).filter((c) => c !== "system").map((c) => <ChannelBadge key={c} channel={c} />)}
            </div>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="employee-thread-messages">
            {thread.messages.length === 0 ? (
              <div className="flex h-full items-center justify-center text-[12px] text-[#555]">No messages yet — start the conversation below.</div>
            ) : (
              thread.messages.map((m) => <MessageBubble key={m.id} msg={m} employee={employee} />)
            )}
            <div ref={bottomRef} />
          </div>

          {/* take-over composer */}
          <div className="border-t border-[#1e1e1e] p-3">
            <div className="mb-2 flex items-center gap-2 text-[10.5px] text-[#6b6b6b]">
              <span className="rounded-md border border-[rgba(245,158,11,0.28)] bg-[rgba(245,158,11,0.10)] px-1.5 py-[1px] text-[#FBBF24]">Take over</span>
              Send {first} a message yourself — pick the channel.
            </div>
            <div className="flex items-end gap-2 rounded-2xl border border-[#2a2a2a] bg-[#161616] p-2 focus-within:border-[rgba(79,142,247,0.4)]">
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="channel-select" className="h-8 w-[118px] shrink-0 border-[#2a2a2a] bg-[#0d0d0d] text-[12px] text-white">
                  <SelectValue><ChannelChoice value={channel} /></SelectValue>
                </SelectTrigger>
                <SelectContent className="border-[#222] bg-[#111] text-white">
                  {["slack", "teams", "text", "email", "call"].map((c) => (
                    <SelectItem key={c} value={c} data-testid={`channel-option-${c}`} className="focus:bg-[#1c1c1c] focus:text-white">
                      <ChannelChoice value={c} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={composerPlaceholder} rows={1} data-testid="employee-message-input"
                className="min-h-[36px] max-h-32 flex-1 resize-none border-0 bg-transparent p-1.5 text-[13px] text-white placeholder:text-[#555] focus-visible:ring-0" />
              <button onClick={send} disabled={!input.trim() || sending} data-testid="employee-message-send"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl btn-gradient text-white transition-opacity disabled:opacity-30">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* performance summary */}
        <div className="space-y-4 xl:h-[calc(100vh-190px)] xl:overflow-y-auto" data-testid="employee-performance">
          <div className="rounded-[14px] border border-[#222] bg-[#111111] p-4 card-shadow">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Compliance score</span>
              <TrendingUp size={14} style={{ color: scoreColor }} />
            </div>
            <div className="mt-2 flex items-end gap-2">
              <span className="text-[34px] font-semibold leading-none tabular-nums" style={{ color: scoreColor }} data-testid="employee-score">{performance.compliance_score}</span>
              <span className="mb-1 text-[12px] text-[#6b6b6b]">/ 100 · {performance.rating}</span>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#1e1e1e]">
              <div className="h-full rounded-full" style={{ width: `${performance.compliance_score}%`, background: scoreColor }} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <Metric label="Spend (period)" value={fmtUSD(performance.period_spend)} testId="perf-spend" />
            <Metric label="Expenses" value={performance.expense_count} testId="perf-count" />
            <Metric label="Violations" value={performance.violations} tone={performance.violations ? "red" : "green"} testId="perf-violations" />
            <Metric label="Missing receipts" value={performance.pending_receipts} tone={performance.pending_receipts ? "amber" : "green"} testId="perf-pending" />
            <Metric label="Approval rate" value={`${performance.approval_rate}%`} tone="green" testId="perf-approval" />
          </div>

          <div className="rounded-[14px] border border-[#222] bg-[#111111] p-4 card-shadow">
            <div className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#6b6b6b]">
              <ReceiptText size={12} /> Top categories
            </div>
            <div className="space-y-2.5">
              {performance.top_categories.length === 0 ? (
                <div className="text-[12px] text-[#555]">No spend yet.</div>
              ) : performance.top_categories.map((c) => (
                <div key={c.category}>
                  <div className="mb-1 flex items-center justify-between text-[11.5px]">
                    <span className="text-[#c9c9c9]">{c.category}</span>
                    <span className="tabular-nums text-[#A3A3A3]">{fmtUSD(c.amount)}</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#1e1e1e]">
                    <div className="h-full rounded-full bg-[#4F8EF7]" style={{ width: `${Math.max(6, (c.amount / maxCat) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[14px] border border-[#222] bg-[#111111] p-4 card-shadow">
            <div className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#6b6b6b]">
              <CircleCheck size={12} /> Recent activity
            </div>
            <div className="space-y-2">
              {performance.recent_expenses.length === 0 ? (
                <div className="text-[12px] text-[#555]">No recent expenses.</div>
              ) : performance.recent_expenses.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-2 rounded-lg border border-[#1a1a1a] bg-[#0d0d0d] px-2.5 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-[12px] font-medium text-white">{e.merchant}</div>
                    <div className="text-[10px] text-[#6b6b6b]">{e.category} · {e.date}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-[12px] font-medium tabular-nums text-[#c9c9c9]">{fmtUSD(e.amount)}</span>
                    {e.status && <StatusChip status={e.status} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
